import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface CategoryTheme {
  primary: string;
  secondary: string;
  accent: string;
  gradient: string;
  gradientHover: string;
  shadow: string;
  glow: string;
}

interface CategoryThemes {
  [key: string]: CategoryTheme;
}

const categoryThemes: CategoryThemes = {
  meditation: {
    primary: '240 100% 70%', // Blue accent
    secondary: '220 100% 65%',
    accent: '200 100% 75%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(240 100% 75% / 0.1) 0%, hsl(220 100% 70% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(240 100% 70% / 0.3)',
    glow: '0 0 30px hsl(240 100% 70% / 0.5)'
  },
  sleep: {
    primary: '260 80% 70%', // Purple accent
    secondary: '280 85% 65%',
    accent: '240 90% 75%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(260 80% 75% / 0.1) 0%, hsl(280 85% 70% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(260 80% 70% / 0.3)',
    glow: '0 0 30px hsl(260 80% 70% / 0.5)'
  },
  anxiety: {
    primary: '180 85% 65%', // Cyan accent
    secondary: '200 90% 60%',
    accent: '160 80% 70%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(180 85% 70% / 0.1) 0%, hsl(200 90% 65% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(180 85% 65% / 0.3)',
    glow: '0 0 30px hsl(180 85% 65% / 0.5)'
  },
  motivation: {
    primary: '15 90% 65%', // Orange accent
    secondary: '25 95% 60%',
    accent: '35 85% 70%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(15 90% 70% / 0.1) 0%, hsl(25 95% 65% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(15 90% 65% / 0.3)',
    glow: '0 0 30px hsl(15 90% 65% / 0.5)'
  },
  memory: {
    primary: '280 85% 70%', // Violet accent
    secondary: '300 80% 65%',
    accent: '260 90% 75%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(280 85% 75% / 0.1) 0%, hsl(300 80% 70% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(280 85% 70% / 0.3)',
    glow: '0 0 30px hsl(280 85% 70% / 0.5)'
  },
  massage: {
    primary: '320 80% 70%', // Pink accent
    secondary: '340 85% 65%',
    accent: '300 75% 75%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(320 80% 75% / 0.1) 0%, hsl(340 85% 70% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(320 80% 70% / 0.3)',
    glow: '0 0 30px hsl(320 80% 70% / 0.5)'
  },
  be_still: {
    primary: '180 70% 65%', // Teal accent
    secondary: '160 75% 60%',
    accent: '200 65% 70%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(180 70% 70% / 0.1) 0%, hsl(160 75% 65% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(180 70% 65% / 0.3)',
    glow: '0 0 30px hsl(180 70% 65% / 0.5)'
  },
  self_love: {
    primary: '340 85% 70%', // Rose accent
    secondary: '320 90% 65%',
    accent: '360 80% 75%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(340 85% 75% / 0.1) 0%, hsl(320 90% 70% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(340 85% 70% / 0.3)',
    glow: '0 0 30px hsl(340 85% 70% / 0.5)'
  },
  'relationship-decisions': {
    primary: '25 95% 65%', // Orange accent
    secondary: '15 90% 60%',
    accent: '35 85% 70%',
    gradient: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)', // Use theme background
    gradientHover: 'linear-gradient(135deg, hsl(25 95% 70% / 0.1) 0%, hsl(15 90% 65% / 0.1) 100%)',
    shadow: '0 20px 40px hsl(25 95% 65% / 0.3)',
    glow: '0 0 30px hsl(25 95% 65% / 0.5)'
  }
};

interface CategoryThemeContextType {
  currentCategory: string | null;
  theme: CategoryTheme | null;
  setCategory: (categoryId: string) => void;
  resetTheme: () => void;
}

const CategoryThemeContext = createContext<CategoryThemeContextType | undefined>(undefined);

export const CategoryThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [theme, setTheme] = useState<CategoryTheme | null>(null);
  const { actualTheme } = useTheme();

  const setCategory = (categoryId: string) => {
    const categoryTheme = categoryThemes[categoryId];
    if (categoryTheme) {
      setCurrentCategory(categoryId);
      setTheme(categoryTheme);
      applyThemeToDOM(categoryTheme);
    }
  };

  // Re-apply category theme when global theme changes
  useEffect(() => {
    if (theme) {
      applyThemeToDOM(theme);
    }
  }, [actualTheme, theme]);

  const resetTheme = () => {
    setCurrentCategory(null);
    setTheme(null);
    removeThemeFromDOM();
  };

  const applyThemeToDOM = (categoryTheme: CategoryTheme) => {
    const root = document.documentElement;
    
    // Apply category-specific CSS variables
    root.style.setProperty('--category-primary', categoryTheme.primary);
    root.style.setProperty('--category-secondary', categoryTheme.secondary);
    root.style.setProperty('--category-accent', categoryTheme.accent);
    root.style.setProperty('--category-gradient', categoryTheme.gradient);
    root.style.setProperty('--category-gradient-hover', categoryTheme.gradientHover);
    root.style.setProperty('--category-shadow', categoryTheme.shadow);
    root.style.setProperty('--category-glow', categoryTheme.glow);
    
    // Also update primary colors to match category
    root.style.setProperty('--primary', categoryTheme.primary);
    root.style.setProperty('--primary-glow', categoryTheme.accent);
    
    // Update gradients to use category colors
    root.style.setProperty('--gradient-primary', categoryTheme.gradient);
    root.style.setProperty('--gradient-wellness', categoryTheme.gradient);
    
    // Update shadows to use category colors
    root.style.setProperty('--shadow-glow', categoryTheme.glow);
    root.style.setProperty('--shadow-category', categoryTheme.shadow);
    root.style.setProperty('--shadow-category-hover', categoryTheme.glow);
  };

  const removeThemeFromDOM = () => {
    const root = document.documentElement;
    
    // Remove category-specific variables
    root.style.removeProperty('--category-primary');
    root.style.removeProperty('--category-secondary');
    root.style.removeProperty('--category-accent');
    root.style.removeProperty('--category-gradient');
    root.style.removeProperty('--category-gradient-hover');
    root.style.removeProperty('--category-shadow');
    root.style.removeProperty('--category-glow');
  };

  return (
    <CategoryThemeContext.Provider value={{
      currentCategory,
      theme,
      setCategory,
      resetTheme
    }}>
      {children}
    </CategoryThemeContext.Provider>
  );
};

export const useCategoryTheme = () => {
  const context = useContext(CategoryThemeContext);
  if (context === undefined) {
    throw new Error('useCategoryTheme must be used within a CategoryThemeProvider');
  }
  return context;
};